<template>
  <div>
    <ToastPrime position="bottom-left" />

    <div class="card-body pt-0 px-0" style="max-height: 400px; overflow-y: auto;"
      :class="{ 'form-disabled': readonly }">
      <DataTable :value="isHeaderLoading ? dummyRows : rows" class="p-datatable-sm p-datatable-gridlines" scrollable
        table-style="min-width: 50rem">

        <!-- Kolom grid: daftar & urutannya dari config (lihat renderColumns), jadi `visible` di
             config benar-benar menentukan kolom tampil/tidak dan kolom baru di config ikut muncul. -->
        <Column v-for="col in renderColumns" :key="col.key" header-class="bg-light text-center"
          :style="{ minWidth: col.width + 'px' }">
          <template #header>
            {{ col.label }}
            <span v-if="rows.some(r => isColumnMandatoryS(r, col.key))" class="text-danger">*</span>
          </template>
          <template #body="{ data }">
            <Skeleton v-if="isHeaderLoading" />
            <!-- Material Number: search-as-you-type (server-side), detail material diambil saat dipilih -->
            <AutoComplete v-else-if="col.kind === 'material'" v-model="data.material_number"
              :suggestions="optionsFor(data, col.key)" option-label="label" option-value="value"
              :force-selection="false" size="small" placeholder="Search Material Number..." :min-length="2"
              :delay="1000" show-clear :disabled="!isColumnEnabledS(data, col.key)"
              :loading="columnLoadingS(data, col.key)" :invalid="isColumnInvalidFn(data, col.key)" fluid
              @complete="e => searchMaterial(e, data)" @item-select="e => onMaterialNumberSelect(data, e)"
              @clear="onMaterialClear(data)">
              <template #option="{ option }">
                <div>
                  <div style="font-size:0.85rem;font-weight:600">{{ option.label }}</div>
                  <div style="font-size:0.75rem;color:#6b7280">{{ option.short_text }}</div>
                </div>
              </template>
            </AutoComplete>
            <!-- Nilai dari master data / config (mis. Material Type): tampil read-only, tetap ikut dikirim -->
            <InputText v-else-if="col.kind === 'readonly'" :model-value="data[col.key] || ''" size="small"
              placeholder="-" disabled fluid />
            <!-- Select dari data_source (array / detail_array / endpoint) — engine yang load opsinya.
                 Kolom `readonlyandsaved` (mis. Industry Sector) tampil terkunci: disabled + nilai disimpan. -->
            <SelectPrime v-else-if="col.kind === 'select'" v-model="data[col.key]" :options="optionsFor(data, col.key)"
              option-label="label" option-value="value" :placeholder="'Select ' + col.label"
              :disabled="!isColumnEnabledS(data, col.key) || isColumnLockedS(data, col.key)"
              :loading="columnLoadingS(data, col.key)" :invalid="isColumnInvalidFn(data, col.key)" fluid
              @show="onColumnShow(data, col.key)" @update:model-value="v => onColumnChange(data, col.key, v)" />
            <InputText v-else v-model="data[col.key]" size="small" :placeholder="col.label"
              :disabled="!isColumnEnabledS(data, col.key)" :invalid="isColumnInvalidFn(data, col.key)" fluid />
          </template>
        </Column>

        <!-- Action -->
        <Column header-class="bg-light text-center" body-class="text-center"
          :header-style="{ width: '70px', minWidth: '70px' }" :body-style="{ width: '70px', minWidth: '70px' }">
          <template #header>
            <div class="centered-column-header">#</div>
          </template>
          <template #body="{ index }">
            <Skeleton v-if="isHeaderLoading" />
            <ButtonPrime v-else icon="pi pi-trash" size="small" severity="danger" outlined rounded
              :disabled="readonly || rows.length <= 1" @click="removeRow(index)" />
          </template>
        </Column>

        <template #empty>
          <div style="text-align:center;padding:40px 0">
            <img src="/no_data.png" alt="No Data" style="max-width:330px;opacity:0.7" />
            <div style="color:#888;font-size:1.1em;font-weight:500">No Data</div>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Dialog Tracking & Import Excel digerakkan parent (New_McrItem) lewat toolbar di sana —
         jangan pasang instance kedua di sini, dialognya jadi dobel karena state-nya sama. -->
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { mapState } from 'vuex';
import advanceSearchService from '@/services/generalLayout/advanceSearchService';
import formConfigHelper from '@/helpers/New_formConfig';
import materialNumberFormatter from '@/helpers/materialNumberFormatter';
import api from '@/helpers/api';
import useFormConfigEngine from '@/composables/useFormConfigEngine';

// Config form ini TIDAK di-request di sini: New_McrForm sudah mengisinya sekali ke Vuex
// (`mcrDetail.formConfig`, dari baris new_mcr_drafting_config atau form-config sesuai form_code),
// jadi form ini cukup membaca config yang sama dari store — termasuk data_source opsi dropdown.

// Tab block/unblock di form config. Backend bisa menulis nama tab dengan pemisah lain
// ("Block Unblock", "Block/Unblock"), jadi key-nya dinormalkan dulu sebelum dicocokkan.
const BLOCK_UNBLOCK_TAB = 'block_unblock';
const normalizeTabKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]+/g, '_');

// URUTAN kolom = layout hardcode (config cuma atur behavior/visibility). Urutan TIDAK diambil
// dari urutan key config: `columns` disimpan di kolom jsonb PostgreSQL yang tidak menjaga urutan
// key. Kolom yang ada di config tapi tidak terdaftar di sini tetap dirender (ditaruh di belakang),
// jadi kolom baru di config tetap muncul tanpa perlu mengubah form ini.
const COLUMN_ORDER = ['block_unblock', 'material_number', 'material_type', 'level', 'plant', 'manufacturer', 'block_reason', 'cat_industry_sector', 'notes'];

// Label & lebar kolom (layout hardcode — config cuma atur behavior, bukan label).
const COLUMN_LABELS = {
  block_unblock: 'Block / Unblock',
  material_number: 'Material Number',
  material_type: 'Material Type',
  level: 'Level',
  plant: 'Plant',
  manufacturer: 'Manufacturer',
  block_reason: 'Status',
  cat_industry_sector: 'Industry Sector',
  notes: 'Notes',
};

const COLUMN_WIDTHS = {
  block_unblock: 130,
  material_number: 220,
  material_type: 150,
  level: 130,
  plant: 160,
  manufacturer: 220,
  block_reason: 220,
  cat_industry_sector: 180,
  notes: 250,
};

// Kolom dropdown (walau config-nya tidak punya `data_source`) & kolom yang selalu tampil
// read-only karena nilainya dari master data, bukan input user (material_type).
const SELECT_COLUMNS = new Set(['block_unblock', 'level', 'plant', 'manufacturer', 'block_reason', 'cat_industry_sector']);
const READONLY_COLUMNS = new Set(['material_type']);

// Kolom dataset besar: opsinya diambil saat user mengetik (server-side search), bukan di-preload.
const SEARCH_AS_YOU_TYPE_COLUMNS = new Set(['material_number']);

// Key warisan FE di item store: opsi/loading/loaded versi lama disimpan flat di row padahal
// sekarang wadahnya per kolom (`row.columnOptions[column]`, lihat setup). Dibuang supaya tidak
// bentrok dengan wadah baru sekaligus tidak ikut ter-submit.
const LEGACY_ROW_KEYS = ['options', 'loading', 'loaded', 'plantOptions', 'manufacturerOptions', 'materialLookupDetail'];

// State visual per baris yang tidak boleh ikut ter-submit.
const VISUAL_STATE_KEYS = ['columnOptions', 'columnLoading', 'columnLoaded', 'rowErrors'];

export default {
  name: 'BlockUnblockForm',
  props: {
    readonly: { type: Boolean, default: false }
  },
  setup(props) {
    // `vm` dibaca lazy lewat getter -- data() baru ada setelah setup() jalan (pola McrItemFormDialog).
    const vm = getCurrentInstance().proxy;

    // Baris & kolom yang sedang dievaluasi engine. Holder ini SENGAJA non-reaktif (plain object
    // di closure setup): kalau reactive, penugasan saat render memicu re-render loop. `column`
    // dipakai `getDialog()` buat milih wadah opsi kolom yang sedang di-load.
    const rowContext = { row: null, column: null };

    // Wadah opsi/loading/loaded per baris PER KOLOM. Engine menulis ke `dialog.options[field]`
    // dengan `field` = nama field repeatable, sedangkan 1 field repeatable punya banyak kolom
    // select — tanpa di-scope per kolom, opsi tiap kolom saling nimpa. Wadahnya menempel di row
    // (bukan WeakMap) supaya template ikut re-render begitu opsinya datang.
    const bucketKey = (column) => column || '__row';

    // Jalur TULIS (dipakai getDialog()): buat wadahnya kalau belum ada. Selalu dipanggil dari
    // method (bukan saat render), jadi aman menambah key reaktif di sini.
    const storeFor = (row, column) => {
      if (!row) return { options: {}, loading: {}, loaded: {} };
      const key = bucketKey(column);
      row.columnOptions = row.columnOptions || {};
      row.columnLoading = row.columnLoading || {};
      row.columnLoaded = row.columnLoaded || {};
      row.columnOptions[key] = row.columnOptions[key] || {};
      row.columnLoading[key] = row.columnLoading[key] || {};
      row.columnLoaded[key] = row.columnLoaded[key] || {};
      return {
        options: row.columnOptions[key],
        loading: row.columnLoading[key],
        loaded: row.columnLoaded[key],
      };
    };
    // Jalur BACA (dipanggil saat render) — TIDAK menambah key apa pun.
    const readStore = (row, column) => {
      const key = bucketKey(column);
      return {
        options: row?.columnOptions?.[key] || {},
        loading: row?.columnLoading?.[key] || {},
        loaded: row?.columnLoaded?.[key] || {},
      };
    };

    // Cache detail material per baris (sumber opsi `detail_array` & default_value "lookup").
    // Tidak dibaca template, jadi cukup WeakMap non-reaktif. Ini registry cache detail form ini
    // (kontrak: satu tempat) — cuma punya 1 entry, `materialLookupDetail`.
    const materialDetails = new WeakMap();
    // Baris yang material_number-nya sudah benar-benar DIPILIH (bukan baru diketik). Sesuai
    // kontrak: "terisi" dicek lewat flag pilih, bukan langsung nilai form-nya.
    const materialSelected = new WeakSet();

    const engine = useFormConfigEngine({
      vm,
      getFormConfig: () => vm.formConfig,
      // Tiap baris grid = satu "dialog" sendiri, dan tiap kolomnya satu wadah opsi sendiri.
      getDialog: () => {
        const store = storeFor(rowContext.row, rowContext.column);
        return {
          form: rowContext.row || {},
          options: store.options,
          loading: store.loading,
          loaded: store.loaded,
          errors: (rowContext.row && rowContext.row.rowErrors) || {},
        };
      },
      getMcrType: () => vm.mcrHeader?.type || '',
      isView: () => props.readonly,
      getCtx: () => vm.fieldCtxS(rowContext.row),
      // Sumber opsi `detail_array` & default_value "lookup": detail material baris aktif.
      getDetailSources: () => ({ materialLookupDetail: materialDetails.get(rowContext.row) }),
      searchAsYouTypeFields: SEARCH_AS_YOU_TYPE_COLUMNS,
    });

    return { ...engine, rowContext, storeFor, readStore, materialDetails, materialSelected };
  },
  data() {
    return {
      rows: [],
      dummyRows: Array.from({ length: 3 }, () => ({})),
      formConfig: null,
      companyCode: '',
      clientCode: ''
    };
  },
  computed: {
    ...mapState({
      isHeaderLoading: state => state.mcr.isLoading.header,
      mcrHeader: state => state.mcr.mcrHeader,
      allMcrItem: state => state.mcr.allMcrItem || [],
      // Sumber config form: diisi New_McrForm sesuai form_code (bukan fetch di sini).
      mcrDetail: state => state.mcr.mcrDetail,
    }),
    tabKey() {
      return this.resolveTabKey(this.formConfig);
    },
    tabFields() {
      return formConfigHelper.getTabFieldMap(this.formConfig, this.tabKey) || null;
    },
    // Field repeatable pemilik baris grid (di FORM03: field `block_unblock` di tab `block_unblock`).
    // Tiap kolomnya adalah satu field form.
    gridField() {
      return this.resolveGridField(this.formConfig, this.tabKey);
    },
    gridFieldConfig() {
      return this.tabFields?.[this.gridField] || null;
    },
    // Daftar kolom yang dipakai form: dari config, fallback ke daftar kolom yang dirender.
    columns() {
      const columns = this.resolveColumns(this.formConfig);
      return columns.length ? columns : [...COLUMN_ORDER];
    },
    // Kolom yang dirender: urutan layout dulu, kolom config yang belum dikenal ditaruh di belakang.
    orderedColumns() {
      const configured = this.columns;
      const known = COLUMN_ORDER.filter(column => configured.includes(column));
      return [...known, ...configured.filter(column => !COLUMN_ORDER.includes(column))];
    },
    // Kolom grid yang lolos `visible` di config. Selama header masih loading semua kolom
    // ditampilkan supaya skeleton-nya kelihatan.
    renderColumns() {
      return this.orderedColumns
        .filter(column => this.isHeaderLoading || this.isColumnVisibleS(column))
        .map(column => ({
          key: column,
          label: this.columnLabel(column),
          kind: this.columnKind(column),
          width: COLUMN_WIDTHS[column] || 160,
        }));
    },
  },
  watch: {
    // Config form datang dari Vuex (diisi New_McrForm, sudah dinormalisasi di sana). `immediate`
    // dipakai supaya config yang sudah termuat sebelum form ini mount langsung terpakai;
    // watcher-nya menangkap config yang baru selesai dimuat setelahnya.
    'mcrDetail.formConfig': {
      immediate: true,
      handler(val) {
        this.formConfig = val || null;
      },
    },
    // Config selesai dimuat -> opsi & default tiap baris di-resolve ulang dari config.
    formConfig() {
      this.rows.forEach(row => this.refreshRow(row));
    },
    // Baris dibangun dari item MCR di store (MCR004 sudah dimapping khusus di fetchMcrHeader).
    allMcrItem: {
      immediate: true,
      handler(val) {
        const base = (val && val.length > 0) ? [...val] : [this.emptyRow()];
        this.rows = base.map(item => this.normalizeRow(item));
        this.rows.forEach(row => {
          row.material_number_raw = row.material_number_raw || row.material_number || '';
          if (row.material_number_raw) {
            row.material_number = materialNumberFormatter.formatMaterialNumber(row.material_number_raw);
            // Data tersimpan = material sudah dipilih, jadi kolom turunannya boleh aktif.
            this.materialSelected.add(row);
          }
          this.refreshRow(row);
          if (row.material_number_raw) {
            this.fetchMaterialDetail(row, row.material_number_raw);
          }
        });
      }
    }
  },
  mounted() {
    // company/client dipakai ctx config (data_source.params).
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.companyCode = userAuth?.user?.company_code || '';
    this.clientCode = userAuth?.user?.client_code || '';
  },
  methods: {
    // ---------------------------------------------------------------------
    // Config
    // ---------------------------------------------------------------------
    // --- Resolusi config -------------------------------------------------
    // Semuanya fungsi atas 1 KANDIDAT config (config form ini dari store, sesuai form_code).
    // Tab/field-nya tidak pernah di-hardcode: ditelusuri dari bentuk config.
    resolveTabKey(config) {
      const keys = Object.keys(config?.tab_fields || {});
      if (keys.includes(BLOCK_UNBLOCK_TAB)) return BLOCK_UNBLOCK_TAB;
      const matched = keys.find(key => normalizeTabKey(key) === BLOCK_UNBLOCK_TAB);
      if (matched) return matched;
      // Config FORM03 cuma punya 1 tab — kalau penamaan tab di BE beda dari dugaan,
      // pakai tab itu daripada semua kolomnya hilang.
      if (keys.length === 1) return keys[0];
      // Nama tab-nya beda jauh: pakai tab yang paling banyak mendeklarasikan kolom form ini.
      let best = BLOCK_UNBLOCK_TAB;
      let bestScore = 0;
      keys.forEach(key => {
        const score = this.resolveColumnsOfTab(config, key).length;
        if (score > bestScore) {
          bestScore = score;
          best = key;
        }
      });
      return best;
    },
    // Field repeatable di 1 tab (pemilik kolom grid).
    resolveGridField(config, tabKey) {
      const fields = formConfigHelper.getTabFieldMap(config, tabKey) || {};
      if (fields[BLOCK_UNBLOCK_TAB]?.columns) return BLOCK_UNBLOCK_TAB;
      return Object.keys(fields).find(name => fields[name]?.columns) || BLOCK_UNBLOCK_TAB;
    },
    resolveColumnsOfTab(config, tabKey) {
      const columns = formConfigHelper.getTabFieldMap(config, tabKey)?.[this.resolveGridField(config, tabKey)]?.columns;
      return columns && typeof columns === 'object' ? Object.keys(columns) : [];
    },
    resolveColumns(config) {
      return this.resolveColumnsOfTab(config, this.resolveTabKey(config));
    },
    // Context evaluator config per baris — dibaca engine lewat `getCtx`.
    // Kolom terkunci (`readonlyandsaved`): tampil, tidak bisa diubah, nilainya tetap disimpan (mis. Industry Sector).
    isColumnLockedS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return false;
      return formConfigHelper.isColumnReadonlySaved(this.formConfig, this.tabKey, this.gridField, column, this.fieldCtxS(row));
    },
    fieldCtxS(row) {
      return {
        // Bikin semua kolom non-aktif & non-wajib saat form readonly.
        isView: this.readonly,
        getField: (name) => {
          if (name === 'company') return this.companyCode;
          if (name === 'client') return this.clientCode;
          if (name === 'mcr_type') return (this.mcrHeader?.type || '').toUpperCase();
          // `material_number` baru dianggap TERISI setelah user memilih suggestion (`@item-select`),
          // bukan saat baru mengetik — sama seperti `dialog.isMaterialSelected` di McrItemFormDialog.
          // Tanpa ini `enabled`/`depends_on`/`trigger` yang gantung ke material_number aktif duluan
          // pas baru ngetik sebagian, dan teks setengah jadi itu ikut terkirim sebagai params
          // data_source kolom itu sendiri (BE memfilternya persis = 0 hasil). `undefined` juga bikin
          // param-nya tidak dikirim sama sekali (axios buang param undefined).
          // Nilainya tetap value form (`material_number` yang sudah diformat), bukan `_raw` — `_raw`
          // khusus buat filter `source_array` (lihat DECISIONS_LOG "filter source_array harus
          // bandingkan ke material_number_raw").
          if (name === 'material_number') {
            return this.materialSelected.has(row) ? row.material_number : undefined;
          }
          return row?.[name];
        },
        // Dipakai entry `{ param, from, attribute }` di data_source.params.
        getFieldOption: (field, attribute) => {
          const options = this.readStore(row, field).options[this.gridField] || [];
          const selected = options.find(o => String(o.value) === String(row?.[field]));
          return selected ? selected[attribute] : undefined;
        },
        hasLine: () => false,
      };
    },

    // ---------------------------------------------------------------------
    // Evaluasi config — evaluator-nya tetap dari engine (`isColumnVisibleFn`, dst).
    // Engine butuh SATU ctx, sedangkan kondisi kolom FORM03 per baris (block_unblock/level/
    // material_number), jadi baris yang sedang dievaluasi dipasang dulu ke `rowContext`.
    // ---------------------------------------------------------------------
    activate(row, column = null) {
      this.rowContext.row = row || null;
      this.rowContext.column = column;
    },
    columnConfig(column) {
      return formConfigHelper.getColumnConfig(this.formConfig, this.tabKey, this.gridField, column);
    },
    isColumnVisibleS(column) {
      // Kolom yang tidak dideklarasikan config tetap ditampilkan (config belum termuat atau
      // nama key-nya beda) — kolom baru disembunyikan kalau config memang bilang `visible` false.
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      // Belum ada baris: evaluasi pakai ctx form-level saja, supaya header tetap tampil
      // (jangan sampai semua kolom hilang hanya karena rows kosong).
      if (!this.rows.length) return this.isColumnVisibleForRow({}, column);
      // Kolom tampil kalau ADA baris yang memenuhi kondisi visible-nya.
      return this.rows.some(row => this.isColumnVisibleForRow(row, column));
    },
    // Label kolom: layout hardcode; kolom config yang belum terdaftar di-humanize dari key-nya.
    columnLabel(column) {
      return COLUMN_LABELS[column]
        || String(column).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    // Jenis widget per kolom. Kolom ber-`data_source` dirender sebagai dropdown (opsinya tetap
    // di-load engine lewat data_source-nya); `material_type` selalu read-only karena nilainya dari
    // master data, sisanya text biasa.
    columnKind(column) {
      if (column === 'material_number') return 'material';
      if (READONLY_COLUMNS.has(column)) return 'readonly';
      if (SELECT_COLUMNS.has(column) || this.columnConfig(column)?.data_source) return 'select';
      return 'text';
    },
    // Visible-nya 1 kolom di BARIS tertentu (dipakai validasi & penanda error).
    isColumnVisibleForRow(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      this.activate(row, column);
      return this.isColumnVisibleFn(column, this.gridField, this.tabKey);
    },
    isColumnEnabledS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      this.activate(row, column);
      return this.isColumnEnabledFn(column, this.gridField, this.tabKey);
    },
    isColumnMandatoryS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return false;
      this.activate(row, column);
      return this.isColumnMandatoryFn(column, this.gridField, this.tabKey);
    },
    // Opsi dropdown 1 kolom di 1 baris (wadah per kolom; dibaca non-creating saat render).
    // Nilai tersimpan SELALU ikut jadi opsi walau tidak ada di daftar opsi saat ini (opsi baru
    // termuat setelah detail material datang, atau master material sudah berubah). SelectPrime
    // menampilkan kosong kalau `value`-nya tidak ada di `options`, jadi tanpa ini nilai yang jelas
    // ada di DB (mis. `cat_manufacturer`/`plant`) terlihat kosong.
    optionsFor(row, column) {
      const options = this.readStore(row, column).options[this.gridField] || [];
      if (this.columnKind(column) !== 'select') return options;
      const value = row?.[column];
      if (value === null || value === undefined || value === '') return options;
      if (options.some(option => String(option.value) === String(value))) return options;
      return [...options, { value, label: String(value) }];
    },
    // Loading state 1 kolom di 1 baris. Engine men-set-nya sendiri (per kolom, lewat
    // `dialog.loading[field]` = wadah kolom aktif) di jalur data_source endpoint.
    columnLoadingS(row, column) {
      return !!this.readStore(row, column).loading[this.gridField];
    },

    // ---------------------------------------------------------------------
    // Baris
    // ---------------------------------------------------------------------
    emptyRow() {
      return {
        material_number: '',
        material_number_raw: '',
        material_type: '',
        block_unblock: '',
        level: '',
        manufacturer: '',
        block_reason: '',
        plant: '',
        cat_industry_sector: '',
        notes: '',
        // error per kolom untuk 1 baris ini (dibaca isColumnInvalidFn) + state visual
        rowErrors: {},
        columnOptions: {},
        columnLoading: {},
        columnLoaded: {},
      };
    },
    // Item dari store membawa key warisan FE (opsi versi lama per baris) — dibuang supaya
    // tidak ikut ter-submit dan tidak bentrok dengan wadah opsi yang baru.
    normalizeRow(item) {
      const row = { ...this.emptyRow(), ...item };
      LEGACY_ROW_KEYS.forEach(key => { delete row[key]; });
      // Wadah opsi selalu mulai kosong (item store tidak pernah membawa opsi yang valid).
      row.rowErrors = {};
      row.columnOptions = {};
      row.columnLoading = {};
      row.columnLoaded = {};
      return row;
    },
    columnsWithDataSource() {
      const columns = this.gridFieldConfig?.columns || {};
      return Object.entries(columns)
        .filter(([, col]) => col?.data_source)
        .map(([name]) => name);
    },
    // `depends_on` di level KOLOM (helper `isDependencyReady` cuma baca depends_on field).
    // Dibaca lewat ctx supaya kolom `material_number` yang baru diketik belum dianggap terisi.
    columnDependencyReady(row, column) {
      const deps = this.columnConfig(column)?.depends_on;
      if (!Array.isArray(deps) || !deps.length) return true;
      this.activate(row, column);
      const ctx = this.fieldCtxS(row);
      return deps.every(dep => formConfigHelper.isFilledValue(ctx.getField(dep)));
    },
    // Load opsi 1 kolom untuk 1 baris (data_source array / detail_array / endpoint).
    // Engine menulis ke `dialog.options[field]`; `dialog`-nya datang dari getDialog() yang
    // mengembalikan wadah kolom yang baru di-`activate()` di sini.
    async loadColumnOptions(row, column, { force = false, search, mapItem } = {}) {
      if (!this.columnConfig(column)?.data_source) return;
      if (!this.columnDependencyReady(row, column)) return;
      this.activate(row, column);
      await this.loadSelectFieldOptions(this.gridField, {
        tab: this.tabKey,
        column,
        force,
        search,
        mapItem,
      });
    },
    // Load opsi SEMUA kolom select baris ini. Kolom dataset besar tidak dipreload — opsinya
    // diambil saat user mengetik (lihat searchMaterial).
    async loadRowColumnOptions(row, { force = false } = {}) {
      const columns = this.columnsWithDataSource()
        .filter(column => !SEARCH_AS_YOU_TYPE_COLUMNS.has(column));
      await Promise.all(columns.map(column => this.loadColumnOptions(row, column, { force })));
    },
    // Kolom yang opsinya diturunkan dari cache detail material (data_source "detail_array"):
    // harus di-refetch saat detail-nya baru datang, karena fetch detail bisa selesai setelah
    // load awal (mode edit).
    async reloadDetailArrayColumns(row) {
      const columns = Object.entries(this.gridFieldConfig?.columns || {})
        .filter(([, col]) => col?.data_source?.type === 'detail_array')
        .map(([name]) => name);
      await Promise.all(columns.map(column => this.loadColumnOptions(row, column, { force: true })));
    },
    // Reset kolom yang `depends_on` kolom yang berubah: opsi + nilainya dikosongkan, supaya
    // tidak ter-submit dalam keadaan disable dan opsinya ikut di-load ulang (`loaded` mati).
    resetDependentColumns(row, changedColumn) {
      Object.entries(this.gridFieldConfig?.columns || {}).forEach(([name, col]) => {
        if (!Array.isArray(col?.depends_on) || !col.depends_on.includes(changedColumn)) return;
        // Ganti wadahnya: opsi lama hilang & `loaded` mati, jadi opsinya di-load ulang saat refresh.
        row.columnOptions[name] = {};
        row.columnLoading[name] = false;
        row.columnLoaded[name] = false;
        row[name] = '';
      });
    },
    // `default_value` di level KOLOM (versi kolom dari applyStaticDefaultValue/applyLookupDefaults):
    // "static" di-apply kalau kolomnya masih kosong, "lookup" saat trigger-nya berubah.
    // `changedColumn` null (baris baru dimuat) sengaja tidak meng-apply lookup, supaya nilai
    // tersimpan di mode edit tidak tertimpa.
    applyColumnDefaultValues(row, { changedColumn = null } = {}) {
      const ctx = this.fieldCtxS(row);
      Object.entries(this.gridFieldConfig?.columns || {}).forEach(([name, col]) => {
        const entries = col?.default_value;
        if (!Array.isArray(entries) || !entries.length) return;
        if (!this.isColumnEnabledS(row, name)) return;

        const entry = entries.find(e => !e.when || formConfigHelper.evaluateCondition(e.when, ctx));
        if (!entry) return;

        if (entry.type === 'static') {
          if (!formConfigHelper.isFilledValue(row[name])) row[name] = entry.value;
          return;
        }
        if (entry.type !== 'lookup') return;
        if (!this.isLookupDefaultTriggered(entry, changedColumn)) return;

        const detail = this.materialDetails.get(row);
        if (!detail) return;

        // source_array + filter: cari 1 baris yang cocok semua kriteria, ambil source_field-nya.
        if (entry.source_array) {
          const rows = Array.isArray(detail[entry.source_array]) ? detail[entry.source_array] : [];
          const filter = entry.filter || {};
          const match = rows.find(r =>
            Object.entries(filter).every(([rowKey, ctxField]) =>
              String(this.resolveSourcePath(r, rowKey) ?? '') === String(ctx.getField(ctxField) ?? ''))
          );
          // Reset ke kosong kalau trigger ready tapi tidak ada baris yang match.
          const value = this.resolveSourceValue(match, entry.source_field, entry.join);
          row[name] = value !== undefined ? value : '';
          return;
        }

        const value = this.resolveSourceValue(detail, entry.source_field, entry.join);
        if (value !== undefined) row[name] = value;
      });
    },
    // Config boleh menulis `trigger` eksplisit; kalau tidak ada, default_value yang filternya
    // menunjuk kolom material dianggap dipicu saat material berubah.
    isLookupDefaultTriggered(entry, changedColumn) {
      if (!changedColumn) return false;
      const trigger = entry.trigger;
      const triggers = Array.isArray(trigger) ? trigger : (trigger ? [trigger] : []);
      if (triggers.length) return triggers.includes(changedColumn);
      return changedColumn === 'material_number'
        && Object.values(entry.filter || {}).some(value => String(value).includes('material_number'));
    },
    // Handler generic buat `@show` semua dropdown grid (kontrak: jangan bikin onXxxShow per kolom).
    // Opsi baris sudah dipreload saat baris dibentuk/berubah, jadi ini cuma no-op yang aman
    // (`loadSelectFieldOptions` early-return kalau sudah `loaded`).
    onColumnShow(row, column) {
      this.activate(row, column);
      this.onFieldOptionShow(this.gridField, this.tabKey, { column });
    },
    // Dipanggil setelah satu kolom di 1 baris berubah (atau saat baris dimuat).
    async refreshRow(row, changedColumn = null) {
      if (!row) return;
      this.activate(row, changedColumn);

      if (changedColumn) {
        // 1. Kolom yang `depends_on` kolom ini: opsi + nilainya direset (level kolom).
        this.resetDependentColumns(row, changedColumn);
        // 2. Nilai kolom yang jadi non-aktif dikosongkan, supaya tidak ter-submit dalam
        //    keadaan disable (mis. block_reason saat Block/Unblock bukan BLOCK).
        if (!this.readonly) {
          this.columns.forEach(name => {
            if (name !== changedColumn && !this.isColumnEnabledS(row, name)) row[name] = '';
          });
        }
      }

      // Opsi kolom (array / detail_array / endpoint) diambil & disimpan per baris per kolom.
      await this.loadRowColumnOptions(row);
      // default_value level kolom (static & lookup) untuk baris ini.
      this.applyColumnDefaultValues(row, { changedColumn });
    },
    onColumnChange(row, column, value) {
      // Nilai di-set di sini juga (bukan cuma lewat v-model) supaya urutan handler tidak berpengaruh.
      row[column] = value;
      this.refreshRow(row, column);
    },
    // Dipanggil toolbar Add di New_McrItem (nama method disamakan dengan UserForm.addNewItem).
    addNewItem() {
      const last = this.rows[this.rows.length - 1];
      if (last && !this.isRowComplete(last)) {
        this.$toast?.add({ severity: 'warn', summary: 'Incomplete Data', detail: `Please complete row ${this.rows.length} before adding a new row.`, life: 2500 });
        return;
      }
      this.rows.push(this.emptyRow());
      // Ambil balik dari `rows` supaya yang dipakai adalah PROXY reaktif-nya, bukan objek mentah —
      // kalau mentah, penulisan opsi oleh engine tidak memicu re-render (dropdown tetap kosong).
      this.refreshRow(this.rows[this.rows.length - 1]);
    },
    removeRow(index) {
      if (this.rows.length <= 1) return;
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          this.rows.splice(index, 1);
        }
      });
    },

    // ---------------------------------------------------------------------
    // Material number
    // ---------------------------------------------------------------------
    // Suggestion diambil dari data_source kolom `material_number` (server-side search).
    async searchMaterial(event, row) {
      const query = event?.query?.trim();
      // User ngetik lagi -> bukan value yang sudah "dipilih" lagi, jadi kolom yang gantung ke
      // material_number (plant/manufacturer) langsung balik non-aktif & opsinya direset.
      this.materialSelected.delete(row);
      if (!query || query.length < 2) return;
      await this.loadColumnOptions(row, 'material_number', {
        force: true,
        search: query,
        mapItem: (item) => ({
          label: materialNumberFormatter.formatMaterialNumber(item.material_number),
          value: materialNumberFormatter.formatMaterialNumber(item.material_number),
          raw: item.material_number, // nilai asli untuk API
          short_text: item.short_text || '',
        }),
      });
    },
    // Trigger ambil detail material HANYA saat user benar-benar memilih suggestion.
    async onMaterialNumberSelect(row, event) {
      const option = event?.value ?? event;
      const raw = option?.raw ?? option?.value ?? option;
      row.material_number = option?.value ?? raw;
      row.material_number_raw = raw;
      this.materialSelected.add(row);
      this.materialDetails.delete(row);
      await this.fetchMaterialDetail(row, raw);
      this.refreshRow(row, 'material_number');
    },
    onMaterialClear(row) {
      row.material_number = '';
      row.material_number_raw = '';
      this.materialSelected.delete(row);
      this.materialDetails.delete(row);
      this.refreshRow(row, 'material_number');
    },
    // Detail material = sumber opsi `detail_array` (plant/manufacturer) & `default_value` lookup.
    async fetchMaterialDetail(row, materialNumber) {
      try {
        const res = await api(this, advanceSearchService.getMaterialDetail(materialNumber), false);
        const detail = res?.data?.data || res?.data || null;
        if (!detail) return;
        // Guard race: kalau user sudah ganti material lain saat fetch ini berjalan, jangan ditimpa.
        if (row.material_number_raw !== materialNumber) return;
        this.materialDetails.set(row, detail);
        await this.reloadDetailArrayColumns(row);
        // TIDAK apply `default_value` lookup di sini: fungsi ini juga dipanggil saat baris lama
        // dimuat (mode edit), dan default lookup akan MENIMPA nilai tersimpan (mis. `manufacturer`,
        // `plant`, `material_type`) — bahkan mengosongkannya kalau tidak ada baris yang match di
        // detail material. Default lookup tetap jalan di jalur user memilih material, lewat
        // refreshRow(row, 'material_number') di onMaterialNumberSelect().
      } catch {
        this.materialDetails.delete(row);
      }
    },

    // ---------------------------------------------------------------------
    // Validasi & payload
    // ---------------------------------------------------------------------
    // Kolom wajib (visible + enabled) yang masih kosong di 1 baris — cuma dihitung, belum ditulis.
    rowErrorsOf(row) {
      const errors = {};
      this.columns.forEach(column => {
        if (!this.isColumnVisibleForRow(row, column)) return;
        if (!this.isColumnMandatoryS(row, column)) return;
        if (formConfigHelper.isFilledValue(row[column])) return;
        errors[column] = true;
      });
      return errors;
    },
    isRowComplete(row) {
      return Object.keys(this.rowErrorsOf(row)).length === 0;
    },
    validate() {
      // row_rules "min_rows" di level field repeatable.
      const minRows = formConfigHelper.getRowRuleValue(this.formConfig, this.tabKey, this.gridField, 'min_rows');
      let valid = !(typeof minRows === 'number' && this.rows.length < minRows);
      // Error disimpan PER BARIS (`row.rowErrors`) — konvensi yang sama dengan McrItemFormDialog.
      // Template membacanya lewat `isColumnInvalidFn` (engine), jadi tanda merah hilang sendiri
      // begitu kolomnya diisi, tanpa perlu submit ulang.
      this.rows.forEach(row => {
        row.rowErrors = this.rowErrorsOf(row);
        if (Object.keys(row.rowErrors).length) valid = false;
      });
      return valid;
    },
    // Payload untuk dikirim ke BE: material_number pakai raw (leading zero). `id` &
    // `mcr_item_code` tetap ikut saat tersedia (mode edit) supaya backend bisa update
    // baris emcr yang tepat.
    getPayload() {
      return this.rows.map(row => {
        const payload = { ...row };
        payload.material_number = row.material_number_raw || row.material_number;
        delete payload.material_number_raw;
        // buang state visual (opsi/loading per kolom)
        VISUAL_STATE_KEYS.forEach(key => { delete payload[key]; });
        // Kolom yang tidak visible di config tidak ikut dikirim.
        this.columns.forEach(column => {
          if (!this.isColumnVisibleS(column)) delete payload[column];
        });
        return payload;
      });
    },
  }
};
</script>

<style scoped>
:deep(.centered-column-header) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

:deep(.p-inputtext:disabled),
:deep(textarea:disabled),
:deep(.p-select.p-disabled),
:deep(.p-disabled) {
  background-color: #f5f5f5 !important;
  pointer-events: none;
  cursor: not-allowed !important;
}

:deep(.p-datatable-sm .p-datatable-tbody > tr > td) {
  font-size: 0.875rem !important;
  padding: 6px 0.75rem !important;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  white-space: nowrap !important;
}

:deep(.p-datatable .p-datatable-tbody > tr.p-datatable-empty-message > td) {
  border: none !important;
  padding: 0 !important;
}

:deep(.p-select:not(.p-select-editable)) {
  min-height: 38px !important;
  height: 38px !important;
}

:deep(.p-select-sm:not(.p-select-editable) .p-select-label),
:deep(.p-select:not(.p-select-editable) .p-select-label) {
  font-size: 0.875rem !important;
}

:deep(.p-inputtext) {
  font-size: 0.875rem !important;
}
</style>
